import { setItem, getItem, removeItem } from '@/services/secureStorage';
import type { ProfileSessionData } from '@/types/profileTypes';

/**
 * Profile Session Storage Service
 *
 * Manages persistence of user profile completion sessions to allow recovery
 * after page refreshes or browser restarts.
 *
 * IMPORTANT: Only data collection phases are persisted (generalProfile, role profiles).
 * Security-sensitive phases (OTP, password) and decision phases (alternateContact,
 * accountDiscovery) are NOT persisted for security reasons.
 *
 * Sessions expire after 24 hours to prevent stale data.
 */

// Storage keys - properly catalogued
export const PROFILE_STORAGE_KEYS = {
  PROFILE_SESSION: 'profile_session',
  PROFILE_SESSION_TIMESTAMP: 'profile_session_timestamp',
  PROFILE_COMPLETION_PROMPT_SHOWN: 'profile_completion_prompt_shown'
} as const;

// Re-export the type for convenience
export type { ProfileSessionData };

export const profileStorage = {
  /**
   * Save the current profile session to localStorage
   */
  saveSession(data: Omit<ProfileSessionData, 'timestamp'>): void {
    const sessionData: ProfileSessionData = {
      ...data,
      timestamp: Date.now()
    };
    setItem(PROFILE_STORAGE_KEYS.PROFILE_SESSION, JSON.stringify(sessionData));
    setItem(PROFILE_STORAGE_KEYS.PROFILE_SESSION_TIMESTAMP, new Date().toISOString());
  },

  /**
   * Retrieve saved profile session from localStorage
   * Returns null if no session, session is invalid, or older than 24 hours
   */
  getSession(): ProfileSessionData | null {
    try {
      const data = getItem(PROFILE_STORAGE_KEYS.PROFILE_SESSION);
      if (!data) return null;

      const parsed = JSON.parse(data) as ProfileSessionData;

      // Check if session is less than 24 hours old
      const hoursSinceCreation = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);
      if (hoursSinceCreation > 24) {
        this.clearSession();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('[ProfileStorage] Failed to parse saved session:', error);
      this.clearSession();
      return null;
    }
  },

  /**
   * Clear all profile session data from localStorage
   */
  clearSession(): void {
    removeItem(PROFILE_STORAGE_KEYS.PROFILE_SESSION);
    removeItem(PROFILE_STORAGE_KEYS.PROFILE_SESSION_TIMESTAMP);
  },

  /**
   * Mark that the profile completion prompt has been shown
   */
  setPromptShown(shown: boolean): void {
    setItem(PROFILE_STORAGE_KEYS.PROFILE_COMPLETION_PROMPT_SHOWN, shown ? 'true' : 'false');
  },

  /**
   * Check if the profile completion prompt was already shown
   */
  wasPromptShown(): boolean {
    return getItem(PROFILE_STORAGE_KEYS.PROFILE_COMPLETION_PROMPT_SHOWN) === 'true';
  },

  /**
   * Clear the prompt shown flag
   */
  clearPromptShown(): void {
    removeItem(PROFILE_STORAGE_KEYS.PROFILE_COMPLETION_PROMPT_SHOWN);
  }
};

export default profileStorage;
